/**
 * Shared helpers — keep them tiny.
 *
 * Veg / Non-veg labels only make sense for food businesses. The platform now
 * spans services, pharmacy, real estate, etc., so we look at the shop's
 * `business_category_ids` to decide whether to show food-type UI.
 *
 * `business_category_ids` may contain either:
 *   - legacy slugs (e.g. ["food"]) from the seed
 *   - UUIDs assigned through the new admin UI
 *
 * `isFoodShop` accepts an optional `verticals` catalog to resolve UUIDs back
 * to their slug (and follow `parent_id` so a subcategory of Food is also
 * treated as food).
 */
const FOOD_CATEGORY_SLUGS = new Set(["food", "meat", "grocery"]);

export function isFoodShop(shop, verticals = []) {
    if (!shop) return false;
    const ids = shop.business_category_ids || [];
    if (ids.length === 0) return false;

    // Build a lookup once per call so we can map UUID → slug and walk parents.
    const byId = {};
    for (const v of verticals) byId[v.id || v.slug] = v;

    const slugFor = (id) => {
        const v = byId[id];
        if (!v) return id;                                    // legacy slug stored directly
        // If this is a subcategory, fall back to its parent so a sub of Food
        // is still treated as food.
        if (v.parent_id && byId[v.parent_id]) return byId[v.parent_id].slug || v.parent_id;
        return v.slug || id;
    };

    return ids.some((id) => FOOD_CATEGORY_SLUGS.has(slugFor(id)));
}

export function isFoodProduct(product, shop, verticals = []) {
    // A product is food if its shop is food. (Categories live on the shop, not the product.)
    return isFoodShop(shop, verticals);
}
